import React, { memo } from "react";
import styled, { keyframes } from "styled-components";
import { theme } from "uikit";

import { ConnectionLevel } from "../../utils/connectionQuality";

/** Unlit bars on the dark header. */
const INACTIVE_ON_DARK = "#374752";
/** Unlit bars on a light background (login page). */
export const INACTIVE_ON_LIGHT = "#c5ccd1";

const BAR_HEIGHTS = [5, 8, 11, 14];
/** Bars start after the room kept for the alert mark, so they never shift. */
const BARS_X = 6;

const LOOK: Record<ConnectionLevel, { bars: number; color?: string }> = {
	good: { bars: 4, color: theme.colors.success },
	fair: { bars: 3, color: theme.colors.warning },
	unstable: { bars: 2, color: theme.colors.warning },
	poor: { bars: 1, color: theme.colors.error },
	unknown: { bars: 0 },
	offline: { bars: 0 },
	noServer: { bars: 0 },
};

const blink = keyframes`
	0%, 100% { opacity: 1; }
	50% { opacity: 0.2; }
`;

const AlertMark = styled.g`
	animation: ${blink} 1s ease-in-out infinite;

	@media (prefers-reduced-motion: reduce) {
		animation: none;
	}
`;

const SignalIcon = memo(
	({ level, inactiveColor = INACTIVE_ON_DARK }: SignalIcon.Props) => {
		const { bars, color } = LOOK[level];
		const noLink = level === "offline" || level === "noServer";

		return (
			<svg width="24" height="16" viewBox="0 0 24 16" aria-hidden="true">
				{noLink && (
					<AlertMark>
						<rect
							x="0"
							y="2"
							width="3"
							height="9"
							rx="1.5"
							fill={theme.colors.error}
						/>
						<circle
							cx="1.5"
							cy="14.5"
							r="1.5"
							fill={theme.colors.error}
						/>
					</AlertMark>
				)}
				{BAR_HEIGHTS.map((height, i) => (
					<rect
						key={height}
						x={BARS_X + i * 5}
						y={16 - height}
						width="3"
						height={height}
						rx="1"
						fill={i < bars && color ? color : inactiveColor}
					/>
				))}
			</svg>
		);
	},
);

declare namespace SignalIcon {
	interface Props {
		level: ConnectionLevel;
		/** Unlit bars; the default suits the dark header. */
		inactiveColor?: string;
	}
}

export default SignalIcon;
